import React from 'react';
import { View, Modal, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

interface VideoProcessingDialogProps {
  visible: boolean;
  progress: number;
  onCancel?: () => void;
  cancellable?: boolean;
}

const VideoProcessingDialog: React.FC<VideoProcessingDialogProps> = ({
  visible,
  progress,
  onCancel,
  cancellable = true,
}) => {
  const { theme, isDark } = useTheme();
  const percent = Math.max(0, Math.min(100, Math.round(progress * 100)));

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
      <View style={styles.overlay}>
        <View
          style={[
            styles.card,
            {
              backgroundColor: theme.card,
              borderColor: isDark ? 'rgba(255,255,255,0.08)' : theme.border,
            },
          ]}
        >
          <ThemedText style={[styles.title, { color: theme.text }]}>
            Optimizing Video
          </ThemedText>
          <ThemedText style={[styles.subtitle, { color: theme.textSecondary }]}>
            Preparing your video for upload
          </ThemedText>

          <View style={[styles.track, { backgroundColor: isDark ? '#2A2A2A' : theme.borderLight }]}>
            <View
              style={[
                styles.fill,
                {
                  width: `${percent}%`,
                  backgroundColor: theme.accent,
                },
              ]}
            />
          </View>

          <ThemedText style={[styles.percent, { color: theme.accent }]}>{percent}%</ThemedText>

          {percent < 8 && (
            <ActivityIndicator size="small" color={theme.accent} style={styles.spinner} />
          )}

          {cancellable && onCancel ? (
            <TouchableOpacity style={styles.cancelButton} onPress={onCancel} activeOpacity={0.7}>
              <ThemedText style={[styles.cancelText, { color: theme.textSecondary }]}>
                Cancel
              </ThemedText>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.72)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: 360,
    borderRadius: 24,
    borderWidth: 1,
    paddingHorizontal: 24,
    paddingTop: 28,
    paddingBottom: 20,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.3,
    shadowRadius: 20,
    elevation: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 22,
    lineHeight: 20,
  },
  track: {
    width: '100%',
    height: 8,
    borderRadius: 999,
    overflow: 'hidden',
  },
  fill: {
    height: '100%',
    borderRadius: 999,
  },
  percent: {
    marginTop: 14,
    fontSize: 18,
    fontWeight: '700',
  },
  spinner: {
    marginTop: 12,
  },
  cancelButton: {
    marginTop: 16,
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  cancelText: {
    fontSize: 15,
    fontWeight: '600',
  },
});

export default VideoProcessingDialog;
